import { Project } from "@shared/interfaces/project.interface";

/**
 * Per-module progress calculator
 * Returns a safe number (0..100) even if the module key is missing.
 */
export type ModuleKey = 'value_assessment' | 'maturity';

export function calculateModuleProgress(project: Project, moduleKey: ModuleKey): number {
  const completion = project.module_completion?.[moduleKey as keyof typeof project.module_completion];
  return typeof completion === 'number' ? completion : 0;
}

/**
 * Calculate the overall project progress percentage across supported modules.
 * Currently, it computes the rounded average of available module completions.
 * If no supported module has a numeric completion, it returns 0.
 */
export function calculateProjectProgress(project: Project): number {
  const completions: number[] = [];

  if (typeof project.module_completion?.value_assessment === 'number') {
    completions.push(project.module_completion.value_assessment);
  }
  if (typeof project.module_completion?.maturity === 'number') {
    completions.push(project.module_completion.maturity);
  }

  if (completions.length === 0) return 0;

  const avg = completions.reduce((a, b) => a + b, 0) / completions.length;
  return Math.round(avg);
}

/**
 * Get project status based on completion percentage
 */
export function getProjectStatus(completion: number): 'not-started' | 'in-progress' | 'complete' {
  if (completion >= 100) return 'complete';
  if (completion > 0) return 'in-progress';
  return 'not-started';
}